import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import customerTypes from "../../shared/customerTypes.json";

interface CreateCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customer?: any) => void;
}

export default function CreateCustomerDialog({
  open,
  onClose,
  onSuccess,
}: CreateCustomerDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      customerType: "Customer",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string().required("Phone number is required"),
      email: Yup.string().email("Invalid email").nullable(),
      customerType: Yup.string().required("Customer type is required"),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const response = await request.post("/api/createCustomer", values);
        if (response?.data?.status) {
          toast.success("Customer created successfully!");
          onSuccess(response?.data?.data);
          handleClose();
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to create customer",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #10b981, #059669)",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <PersonAddIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Box
          sx={{
            flex: 1,
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          Create Customer
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="name"
            label="Name"
            type="text"
            formik={formik}
            autoFocus
          />
          <TextField name="phone" label="Phone" type="text" formik={formik} />
          <TextField name="email" label="Email" type="text" formik={formik} />
          <SelectField
            name="customerType"
            label="Customer Type"
            formik={formik}
            options={customerTypes}
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: "rgba(0,0,0,0.12)",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          size="small"
          disabled={submitting}
          startIcon={<PersonAddIcon />}
          sx={{
            background: "linear-gradient(135deg, #10b981, #059669)",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2,
            "&:hover": {
              background: "linear-gradient(135deg, #059669, #047857)",
            },
          }}
        >
          {submitting ? "Creating..." : "Create Customer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
